import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import type { Report } from '@simpleacs/shared';
import { api } from '../lib/api';
import { useTasks } from '../lib/tasks';
import { formatTimestamp } from '../lib/format';
import { useSession } from '../lib/session';
import { Button, Pill } from '../components/ui';

/**
 * One finding, opened to act on.
 *
 * The table used to be the whole story: the nightly check would report the
 * same drift for ever and the only way to act was to know which ONU page to
 * go and find. Here the finding shows its evidence — the missing lines or
 * differences, and the commands a fix would run — and offers the two ways to
 * close it: Reconcile (re-state the platform's intent to the chassis, the
 * same plan the ONU page's Resync runs) or Dismiss (close it without acting,
 * keeping the evidence).
 */
export function ReportDetailDialog({
  report,
  onClose,
}: {
  report: Report;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const tasks = useTasks();
  const { can } = useSession();

  const detail = (report.detail ?? {}) as {
    missing?: string[];
    expected?: string[];
    changes?: string[];
    commands?: string[];
    reconcile?: { at: number; by: string; rejected?: string[] };
  } & Record<string, unknown>;

  /**
   * Reconcile is the ONU page's Resync by another name — a full chassis session
   * — so it CLOSES the finding dialog and runs in the background tray, rather
   * than pinning the operator to a spinner. The reports list is refreshed on
   * either outcome (a rejection closes the finding as fix_failed).
   */
  // A router that stopped matching what was stored (2026-09-25). Its Apply
  // re-applies the stored settings to the ROUTER, not the chassis.
  const routerDrift = report.kind === 'router_drift';
  // A dormant ONU warned before its deletion (2026-10-13): Dismiss is "keep".
  const dormant = report.kind === 'onu_dormant_deletion';

  const startReconcile = () => {
    const subject = report.subject;
    const onuLink = report.onuId ?? report.deviceId;
    onClose();
    tasks.start({
      label: `Reconcile ${subject}`,
      ...(onuLink ? { href: `/onus/${encodeURIComponent(onuLink)}` } : {}),
      run: async () => {
        try {
          return await api.post(`/reports/${encodeURIComponent(report.id)}/reconcile`);
        } finally {
          await queryClient.invalidateQueries({ queryKey: ['reports'] });
        }
      },
      done: () =>
        routerDrift
          ? `${subject}: the stored settings are re-applied to the router at its next inform.`
          : `${subject} reconciled — the platform's intent was re-stated to the chassis.`,
    });
  };

  /**
   * Dismiss goes the same way as Reconcile: the dialog closes on the click and
   * the tray reports the outcome, so a refused dismiss is not lost with it.
   */
  const startDismiss = () => {
    const subject = report.subject;
    onClose();
    tasks.start({
      label: dormant ? `Keep ${subject}` : `Dismiss finding — ${subject}`,
      run: () => api.post(`/reports/${encodeURIComponent(report.id)}/dismiss`),
      done: () =>
        dormant
          ? `${subject} is kept — it will not be deleted while it stays offline.`
          : `${subject} dismissed. The evidence stays on the row.`,
      invalidate: [['reports']],
    });
  };

  const drift = report.kind === 'config_drift' || routerDrift;
  const open = report.status === 'open';
  const onuLink = report.onuId ?? report.deviceId;
  const lines = detail.missing ?? detail.changes ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl border border-line bg-surface shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">
              <span className="font-mono">{report.subject}</span>
              <Pill state
                tone={
                  report.status === 'open'
                    ? 'warn'
                    : report.status === 'fixed'
                      ? 'success'
                      : report.status === 'fix_failed'
                        ? 'danger'
                        : 'neutral'
                }
              >
                {report.status.replace('_', ' ')}
              </Pill>
            </h2>
            <p className="mt-0.5 text-xs text-muted">
              {report.summary} · found {formatTimestamp(report.createdAt)}
            </p>
          </div>
          {onuLink && (
            <Link
              to={`/onus/${encodeURIComponent(onuLink)}`}
              className="inline-flex shrink-0 items-center gap-1.5 text-xs text-primary hover:underline"
            >
              Open the ONU <ExternalLink className="size-3.5" />
            </Link>
          )}
        </header>

        <div className="max-h-[55vh] space-y-4 overflow-y-auto p-5">
          {lines.length > 0 && (
            <section>
              <h3 className="text-xs uppercase tracking-wide text-faint">
                {detail.missing ? 'Configured lines missing on the ONU' : 'Differences found'}
              </h3>
              <pre className="mt-1.5 overflow-x-auto rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs leading-relaxed text-ink">
                {lines.join('\n')}
              </pre>
            </section>
          )}

          {detail.commands && detail.commands.length > 0 && (
            <section>
              <h3 className="text-xs uppercase tracking-wide text-faint">
                Commands a reconcile would run
              </h3>
              <pre className="mt-1.5 overflow-x-auto rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs leading-relaxed text-muted">
                {detail.commands.join('\n')}
              </pre>
            </section>
          )}

          {detail.reconcile && (
            <section>
              <h3 className="text-xs uppercase tracking-wide text-faint">Reconciled</h3>
              <p className="mt-1 text-xs text-muted">
                {formatTimestamp(detail.reconcile.at)} by {detail.reconcile.by}
              </p>
              {detail.reconcile.rejected && (
                <pre className="mt-1.5 overflow-x-auto rounded-lg border border-danger/40 bg-surface-2 p-3 font-mono text-xs text-danger">
                  {detail.reconcile.rejected.join('\n')}
                </pre>
              )}
            </section>
          )}
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-5 py-3">
          <p className="text-[11px] text-faint">
            {dormant && open
              ? 'Keep stops this deletion. The ONU stays as it is for as long as it stays offline; if it comes back and later goes dormant again, it is warned again.'
              : routerDrift && open
              ? 'Apply puts back what SimpleACS stored for this router. Dismiss keeps the router’s value — the notice stays down until the difference changes.'
              : drift && open
                ? 'Reconcile re-states what SimpleACS holds — the same commands as the ONU page’s Resync. Nothing is removed.'
                : 'Closed findings keep their evidence.'}
          </p>
          <span className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            {open && can('drift.resolve') && (
              <>
                <Button variant="secondary" onClick={startDismiss}>
                  {dormant ? 'Keep' : 'Dismiss'}
                </Button>
                {drift && report.onuId && (
                  <Button variant="primary" onClick={startReconcile}>
                    Apply
                  </Button>
                )}
              </>
            )}
          </span>
        </footer>
      </div>
    </div>
  );
}
